import React, { useCallback, useState } from 'react'
import Tippy from '@tippyjs/react'

import Icon from '../ui/Icon'

interface TextareaFormProps {
  id?: string
  name: string
  value: string
  required?: boolean
  label?: string
  placeHolder?: string
  disabled?: boolean
  infoMessage?: string
  errorMessage?: string
  className?: string
  minLength?: number
  maxLength?: number
  rows?: number
  onChange: (name: string, value: string, isValid: boolean) => void
  onValidate?: (value: string) => boolean
}

export const TextareaForm = ({
  id,
  name,
  value,
  required = false,
  label,
  placeHolder = '',
  disabled = false,
  infoMessage,
  errorMessage,
  className = '',
  minLength,
  maxLength,
  rows = 4,
  onChange,
  onValidate,
}: TextareaFormProps) => {
  const [isError, setIsError] = useState(false)
  const [isTouch, setIsTouch] = useState(false)
  const [isFocus, setIsFocus] = useState(false)

  const isRequired = isTouch && required && !value.trim()

  const validate = useCallback(
    (currentValue: string, nativeValid = true) => {
      const hasValue = currentValue.trim().length > 0

      if (!hasValue) {
        return !required
      }

      const customValid = onValidate ? onValidate(currentValue) : true
      return nativeValid && customValid
    },
    [required, onValidate]
  )

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setIsTouch(true)
      const currentValue = event.target.value
      const valid = validate(currentValue, event.target.validity.valid)
      setIsError(!valid)
      onChange(name, currentValue, valid)
    },
    [name, onChange, validate]
  )

  const handleBlur = () => {
    setIsFocus(false)
    setIsTouch(true)
    setIsError(!validate(value))
  }

  const fieldClass = [
    'field',
    required && 'required',
    !isFocus && isRequired && 'is-required',
    !isFocus && isError && 'is-error',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={fieldClass}>
      {label && <label htmlFor={id}>{label}</label>}
      <div className="input-icon-wrap">
        <textarea
          id={id}
          name={name}
          className="input"
          value={value}
          rows={rows}
          minLength={minLength}
          maxLength={maxLength}
          placeholder={!disabled ? placeHolder : ''}
          required={required}
          disabled={disabled}
          onChange={handleChange}
          onFocus={() => setIsFocus(true)}
          onBlur={handleBlur}
        />
        {infoMessage && (
          <Tippy content={infoMessage} placement="top" className="ph-tooltip">
            <div className="input-icon-toggle">
              <Icon name="info" size={16} />
            </div>
          </Tippy>
        )}
      </div>
      {errorMessage && !isFocus && isError && (
        <div className="field-msg err" role="alert">
          <span className="ico">
            <Icon name="warn" size={13} />
          </span>
          {errorMessage}
        </div>
      )}
    </div>
  )
}
